import {DashboardUpdates} from './DashboardUpdates';
import {tx, ptx, usePageText} from '../lib/page-text';
import { LiveHelp } from './help/LiveHelp';
import { topicForPath } from './help/guides';
import type { ReactElement } from "react";
import { useEffect, useState } from "react";
import { NavLink, Outlet, useLocation } from "../lib/localized-router";
import { useAuth } from "../lib/auth-store";
import { useT } from "../lib/i18n";
import { AccountMenu } from "./AccountMenu";
import {
  HomeIcon,
  ProjectsIcon,
  SessionsIconNav,
  ChatIcon,
  ScenariosIcon,
  StreamIcon,
  StudioIcon,
  RecordingsIcon,
  RestreamIcon,
  PremiereIcon,
  ClipIcon,
  CatalogIcon,
  KeyIcon,
  WebhookIcon,
  FlaskIcon,
  NetworkIcon,
  EmbedIcon,
  DocsIcon,
  ChartIcon,
  SettingsIcon,
  CodeIcon,
} from "./icons";

interface NavItem {
  to: string;
  labelKey: string;
  icon: () => ReactElement;
  external?: boolean;
}
interface Section {
  id: string;
  titleKey: string;
  icon: () => ReactElement;
  items: NavItem[];
}
const sections: Section[] = [
  {
    id: "live",
    titleKey: "nav.streams",
    icon: StreamIcon,
    items: [
      { to: "/streams", labelKey: "nav.allStreams", icon: StreamIcon },
      { to: "/studio", labelKey: "nav.studio", icon: StudioIcon },
      { to: "/restream", labelKey: "nav.destinations", icon: RestreamIcon },
      { to: "/premieres", labelKey: "nav.premieres", icon: PremiereIcon },
    ],
  },
  {
    id: "commerce",
    titleKey: "nav.ecommerce",
    icon: CatalogIcon,
    items: [
      { to: "/commerce/catalog", labelKey: "nav.products", icon: CatalogIcon },
      {
        to: "/commerce/services",
        labelKey: "nav.services",
        icon: SessionsIconNav,
      },
      {
        to: "/commerce/collections",
        labelKey: "nav.collections",
        icon: ProjectsIcon,
      },
      { to: "/commerce/orders", labelKey: "nav.orders", icon: ChartIcon },
      {
        to: "/commerce/settings",
        labelKey: "nav.commerceSettings",
        icon: SettingsIcon,
      },
    ],
  },
  {
    id: "engagement",
    titleKey: "nav.engagement",
    icon: ChatIcon,
    items: [
      { to: "/sessions", labelKey: "nav.sessions", icon: SessionsIconNav },
      { to: "/chat", labelKey: "nav.chat", icon: ChatIcon },
      { to: "/boards", labelKey: "nav.boards", icon: ScenariosIcon },
    ],
  },
  {
    id: "content",
    titleKey: "nav.content",
    icon: RecordingsIcon,
    items: [
      { to: "/recordings", labelKey: "nav.recordings", icon: RecordingsIcon },
      { to: "/clips", labelKey: "nav.clips", icon: ClipIcon },
      { to: "/blueprints", labelKey: "nav.streamTemplates", icon: CodeIcon },
    ],
  },
  {
    id: "developer",
    titleKey: "nav.developer",
    icon: CodeIcon,
    items: [
      { to: "/api-keys", labelKey: "nav.apiKeys", icon: KeyIcon },
      { to: "/webhooks", labelKey: "nav.webhooks", icon: WebhookIcon },
      { to: "/webhook-tester", labelKey: "nav.webhookTester", icon: FlaskIcon },
      { to: "/network-test", labelKey: "nav.networkTest", icon: NetworkIcon },
      { to: "/embed", labelKey: "nav.embed", icon: EmbedIcon },
      { to: "/scenarios", labelKey: "nav.scenarios", icon: ScenariosIcon },
      {
        to: "/Live/Developer/",
        labelKey: "nav.docs",
        icon: DocsIcon,
        external: true,
      },
    ],
  },
];

function NavigationGroup({
  section,
  close,
}: {
  section: Section;
  close: () => void;
}) {
  usePageText();
  const { t } = useT();
  const { pathname } = useLocation();
  const active = section.items.some(
    (item) =>
      !item.external &&
      (pathname === item.to || pathname.startsWith(item.to + "/")),
  );
  const storageKey = "kardolive.nav." + section.id;
  const [open, setOpen] = useState(() => {
    try {
      return active || localStorage.getItem(storageKey) === "open";
    } catch {
      return active;
    }
  });
  useEffect(() => {
    if (active) setOpen(true);
  }, [active, pathname]);
  return (
    <div className="nav-group">
      <button
        className={`nav-group-toggle ${active ? "current" : ""}`}
        aria-expanded={open}
        aria-controls={"nav-" + section.id}
        onClick={() => {
          setOpen(!open);
          try {
            localStorage.setItem(storageKey, !open ? "open" : "closed");
          } catch {
            /* storage unavailable */
          }
        }}
      >
        <section.icon />
        <span>{t(section.titleKey)}</span>
        <span className="nav-chevron" aria-hidden="true">
          {open ? "⌄" : "›"}
        </span>
      </button>
      <div className="nav-children" id={"nav-" + section.id} hidden={!open}>
        {section.items.map((item) =>
          item.external ? (
            <a key={item.to} href={item.to} target="_blank" rel="noopener">
              <item.icon />
              {t(item.labelKey)}
            </a>
          ) : (
            <NavLink
              key={item.to}
              to={item.to}
              onClick={close}
              className={({ isActive }) => (isActive ? "active" : "")}
            >
              <item.icon />
              {t(item.labelKey)}
            </NavLink>
          ),
        )}
      </div>
    </div>
  );
}

export function Layout() {
  usePageText();
  const { tenant } = useAuth();
  const location = useLocation();
  const [menuOpen, setMenuOpen] = useState(false);
  const { t } = useT();
  useEffect(() => {
    setMenuOpen(false);
  }, [location.pathname]);
  useEffect(() => {
    const close = (e: KeyboardEvent) => {
      if (e.key === "Escape") setMenuOpen(false);
    };
    window.addEventListener("keydown", close);
    return () => window.removeEventListener("keydown", close);
  }, []);

  return (
    <div className="layout">
      <a className="skip-to-content" href="#workspace-content" onClick={e => { e.preventDefault(); const content = document.getElementById('workspace-content'); content?.focus({ preventScroll: true }); content?.scrollIntoView({ block: 'start' }); }}>{t('nav.skipContent')}</a>
      {menuOpen && (
        <button
          className="menu-backdrop"
          aria-label={t("nav.closeMenu")}
          onClick={() => setMenuOpen(false)}
        />
      )}
      <aside
        className={`sidebar ${menuOpen ? "mobile-open" : ""}`}
        id="workspace-menu"
      >
        <div className="brand">
          <img
            src="https://kardocloud.com/brand/logo-horizontal-480.png"
            alt={tx("Kardolive")}
          />
        </div>
        <nav className="workspace-nav" aria-label={t("nav.menu")}>
          <NavLink to="/" end onClick={() => setMenuOpen(false)}>
            <HomeIcon />
            {t("nav.overview")}
          </NavLink>
          <NavLink to="/projects" onClick={() => setMenuOpen(false)}>
            <ProjectsIcon />
            {t("nav.projects")}
          </NavLink>
          {sections
            .filter((s) => s.id !== "developer")
            .map((section) => (
              <NavigationGroup
                key={section.id}
                section={section}
                close={() => setMenuOpen(false)}
              />
            ))}
          <NavLink to="/analytics" onClick={() => setMenuOpen(false)}>
            <ChartIcon />
            {t("nav.analytics")}
          </NavLink>
          <NavigationGroup
            section={sections.find((s) => s.id === "developer")!}
            close={() => setMenuOpen(false)}
          />
          <NavLink to="/support" onClick={() => setMenuOpen(false)}><ChatIcon />{tx("Support")}</NavLink>
          <NavLink to="/settings" onClick={() => setMenuOpen(false)}>
            <SettingsIcon />
            {t("nav.settings")}
          </NavLink>
        </nav>
      </aside>
      <div className="main">
        <header className="topbar">
          <button
            className="btn outline sm mobile-menu-button"
            aria-label={t("nav.menu")}
            aria-controls="workspace-menu"
            aria-expanded={menuOpen}
            onClick={() => setMenuOpen((v) => !v)}
          >
            ☰
          </button>
          <div className="breadcrumbs dashboard-workspace" title={tenant?.name}>
            {tenant?.name}
            <span className="muted dashboard-plan">
              {tenant?.plan?.toLowerCase()}
            </span>
          </div>
          <div className="dashboard-utilities">
            <DashboardUpdates />
            <LiveHelp topic={topicForPath(location.pathname)} compact />
            <AccountMenu />
          </div>
        </header>
        <main className="content" id="workspace-content" tabIndex={-1}>
          <Outlet />
        </main>
      </div>
    </div>
  );
}
